/* ==========================================================================
   CALEROA — Interacciones
   Vanilla JS, sin librerías. Una función init por componente; cada una sale
   sola si su elemento no existe, para poder cargar el archivo en todas las
   páginas.
   ========================================================================== */

(function () {
  'use strict';

  var SIN_MOVIMIENTO = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Reveals al hacer scroll — un único observador compartido
     --------------------------------------------------------------------- */
  function initReveals() {
    var elementos = Array.prototype.filter.call(
      document.querySelectorAll('.cal-reveal, .cal-reveal-media'),
      function (el) { return primeraVez(el, 'Reveal'); }
    );
    if (!elementos.length) return;

    if (SIN_MOVIMIENTO || !('IntersectionObserver' in window)) {
      elementos.forEach(function (el) { el.classList.add('cal-visible'); });
      return;
    }

    var observador = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (entrada) {
        if (!entrada.isIntersecting) return;
        entrada.target.classList.add('cal-visible');
        observador.unobserve(entrada.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    elementos.forEach(function (el) { observador.observe(el); });

    // Red de seguridad: si por cualquier motivo el observador no llega a
    // dispararse (pestaña en segundo plano, navegador raro, un error ajeno),
    // el contenido NO puede quedarse invisible. A los 2,5 s se muestra todo
    // lo que siga oculto por encima del pliegue.
    window.setTimeout(function () {
      elementos.forEach(function (el) {
        if (el.classList.contains('cal-visible')) return;
        var caja = el.getBoundingClientRect();
        if (caja.top < window.innerHeight * 1.5) el.classList.add('cal-visible');
      });
    }, 2500);
  }

  var FOCO = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* El editor de Shopify vuelve a llamar a arrancar() cada vez que recarga una
     sección. Sin esta marca, cada recarga añadiría otro juego de listeners
     sobre los mismos elementos.

     La marca lleva SIEMPRE el nombre del componente. Un mismo elemento puede
     pertenecer a dos componentes a la vez —el bloque de compra es también un
     reveal, y el marco del vídeo también— y con una marca compartida el primero
     en inicializarse dejaba al segundo fuera en silencio. Eso es lo que tenía
     muerto el selector de cantidad y el botón de reproducir. */
  var faqEscuchandoResize = false;

  function primeraVez(el, componente) {
    if (!el) return false;
    var clave = 'calInit' + (componente || 'General');
    if (el.dataset[clave] === '1') return false;
    el.dataset[clave] = '1';
    return true;
  }

  /* Las fotografías que se van turnando (ritual, explorador) van con carga
     diferida, pero están apiladas y ocultas: el navegador no las pide hasta
     que les toca, y en ese momento se ve el hueco beige del fondo. Al cambiar
     de una, se le pide ya la siguiente para que llegue a tiempo. */
  function adelantarImagen(figura) {
    if (!figura) return;
    var img = figura.querySelector('img');
    if (img && !img.complete) img.loading = 'eager';
  }

  /* ---------------------------------------------------------------------
     Cabecera — mega menú, menú de móvil y alto real de la barra
     --------------------------------------------------------------------- */
  function initHeader() {
    var cabecera = document.querySelector('[data-cal-header]');
    if (!cabecera || !primeraVez(cabecera, 'Header')) return;

    /* --- Alto real de la cabecera, para que el hero pueda restarlo --- */
    function medir() {
      var alto = cabecera.getBoundingClientRect().height;
      if (alto) document.documentElement.style.setProperty('--cal-header-alto', Math.round(alto) + 'px');
    }
    medir();
    window.addEventListener('resize', medir);
    if ('ResizeObserver' in window) new ResizeObserver(medir).observe(cabecera);

    /* --- Filete al separarse de lo alto de la página --- */
    var desplazado = false;
    function alDesplazar() {
      var ahora = window.scrollY > 8;
      if (ahora === desplazado) return;
      desplazado = ahora;
      cabecera.classList.toggle('is-desplazado', ahora);
    }
    alDesplazar();
    window.addEventListener('scroll', alDesplazar, { passive: true });

    /* --- Mega menú --- */
    var conCursor = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    cabecera.querySelectorAll('[data-cal-mega]').forEach(function (item) {
      var boton = item.querySelector('[data-cal-mega-boton]');
      if (!boton) return;

      var temporizador = null;

      function abrir() {
        window.clearTimeout(temporizador);
        item.classList.add('is-abierto');
        boton.setAttribute('aria-expanded', 'true');
      }
      function cerrar() {
        item.classList.remove('is-abierto');
        boton.setAttribute('aria-expanded', 'false');
      }
      function alternar() {
        if (boton.getAttribute('aria-expanded') === 'true') cerrar(); else abrir();
      }

      // Clic y toque: siempre. Es el único camino en pantallas táctiles.
      boton.addEventListener('click', function (e) {
        e.preventDefault();
        alternar();
      });

      // Cursor: solo donde hay cursor de verdad.
      if (conCursor) {
        item.addEventListener('mouseenter', abrir);
        item.addEventListener('mouseleave', function () {
          temporizador = window.setTimeout(cerrar, 120);
        });
      }

      // Teclado: Escape cierra y devuelve el foco al botón.
      item.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (boton.getAttribute('aria-expanded') !== 'true') return;
        cerrar();
        boton.focus();
      });

      // Al salir del componente con el tabulador, se cierra.
      item.addEventListener('focusout', function (e) {
        if (item.contains(e.relatedTarget)) return;
        cerrar();
      });

      // Un clic fuera lo cierra.
      document.addEventListener('click', function (e) {
        if (item.contains(e.target)) return;
        cerrar();
      });
    });

    /* --- Menú de móvil --- */
    var menu = cabecera.querySelector('[data-cal-menu]');
    var abrirMenu = cabecera.querySelector('[data-cal-menu-abrir]');

    if (menu && abrirMenu) {
      var panel = menu.querySelector('.cal-menu__panel');
      var devolverFoco = null;

      function abrirCajon() {
        devolverFoco = document.activeElement;
        menu.classList.add('is-abierto');
        abrirMenu.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        var primero = panel.querySelector(FOCO);
        if (primero) primero.focus();
      }

      function cerrarCajon() {
        menu.classList.remove('is-abierto');
        abrirMenu.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        if (devolverFoco && devolverFoco.focus) devolverFoco.focus();
      }

      abrirMenu.addEventListener('click', abrirCajon);
      menu.querySelectorAll('[data-cal-menu-cerrar]').forEach(function (b) {
        b.addEventListener('click', cerrarCajon);
      });

      menu.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { cerrarCajon(); return; }
        if (e.key !== 'Tab') return;

        // El foco no debe escaparse del cajón mientras está abierto
        var focables = Array.prototype.filter.call(
          panel.querySelectorAll(FOCO),
          function (el) { return el.offsetParent !== null; }
        );
        if (!focables.length) return;
        var primero = focables[0];
        var ultimo = focables[focables.length - 1];

        if (e.shiftKey && document.activeElement === primero) {
          e.preventDefault(); ultimo.focus();
        } else if (!e.shiftKey && document.activeElement === ultimo) {
          e.preventDefault(); primero.focus();
        }
      });

      // Al navegar a una ficha, el cajón no debe quedarse abierto
      menu.querySelectorAll('a[href]').forEach(function (a) {
        a.addEventListener('click', cerrarCajon);
      });

      // Acordeón de productos dentro del cajón
      menu.querySelectorAll('[data-cal-menu-acordeon]').forEach(function (boton) {
        var destino = document.getElementById(boton.getAttribute('aria-controls'));
        if (!destino) return;
        boton.addEventListener('click', function () {
          var abierto = boton.getAttribute('aria-expanded') === 'true';
          boton.setAttribute('aria-expanded', abierto ? 'false' : 'true');
          destino.classList.toggle('is-abierto', !abierto);
        });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Selector de aromas — el aroma activo ocupa el centro; el resto se
     insinúa alrededor y detrás. Flechas, teclado, swipe y clic directo.
     --------------------------------------------------------------------- */
  function colocarAromas(pista, activo) {
    var frascos = pista.querySelectorAll('.cal-aroma');
    var total = frascos.length;
    if (!total) return;

    frascos.forEach(function (frasco, i) {
      // desplazamiento circular con signo: -2, -1, 0, 1, 2, 3...
      var salto = i - activo;
      if (salto >  total / 2) salto -= total;
      if (salto < -total / 2) salto += total;

      var distancia = Math.abs(salto);
      var signo = salto === 0 ? 0 : (salto > 0 ? 1 : -1);
      var esCentro = distancia === 0;
      // el más lejano se esconde DETRÁS del central, no a los lados
      var esTrasero = distancia >= 3;

      var x, y, escala, opacidad, desenfoque, capa;

      if (esCentro) {
        x = 0;    y = 0;   escala = 1;    opacidad = 1;   desenfoque = 0; capa = 30;
      } else if (esTrasero) {
        x = 0;    y = -9;  escala = 0.34; opacidad = 0.13; desenfoque = 5; capa = 5;
      } else if (distancia === 1) {
        x = signo * 64; y = -2; escala = 0.58; opacidad = 0.52; desenfoque = 2; capa = 20;
      } else {
        x = signo * 106; y = -5; escala = 0.42; opacidad = 0.26; desenfoque = 4; capa = 10;
      }

      frasco.style.transform = 'translate(-50%, -50%) translate(' + x + '%, ' + y + '%) scale(' + escala + ')';
      frasco.style.opacity = opacidad;
      frasco.style.filter = desenfoque ? 'blur(' + desenfoque + 'px)' : 'none';
      frasco.style.zIndex = capa;
      frasco.classList.toggle('is-activo', esCentro);
      frasco.setAttribute('aria-hidden', esCentro ? 'false' : 'true');
      frasco.tabIndex = esCentro ? 0 : -1;
      // solo los laterales cercanos invitan al clic
      frasco.style.pointerEvents = distancia <= 2 ? 'auto' : 'none';
    });
  }

  function initSelectorAromas() {
    var selectores = document.querySelectorAll('[data-cal-aromas]');
    if (!selectores.length) return;

    selectores.forEach(function (raiz) {
      if (!primeraVez(raiz, 'SelectorAromas')) return;
      var pista    = raiz.querySelector('.cal-aromas__pista');
      var frascos  = raiz.querySelectorAll('.cal-aroma');
      var nombre   = raiz.querySelector('[data-cal-aroma-nombre]');
      var texto    = raiz.querySelector('[data-cal-aroma-texto]');
      var anuncio  = raiz.querySelector('[data-cal-aroma-anuncio]');
      var puntos   = raiz.querySelectorAll('[data-cal-aroma-punto]');
      var marcas   = raiz.querySelectorAll('[data-cal-aroma-marca]');
      var antes    = raiz.querySelector('[data-cal-aroma-antes]');
      var despues  = raiz.querySelector('[data-cal-aroma-despues]');
      var total    = frascos.length;
      if (!pista || total < 2) return;

      var activo = 0;

      /* Antes de girar, los dos frascos vecinos dejan de ser diferidos: así el
         siguiente cambio nunca enseña un hueco mientras se descarga la foto. */
      function precargarVecinos(centro) {
        [-1, 1, -2, 2].forEach(function (salto) {
          var i = (centro + salto + total) % total;
          var img = frascos[i].querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        });
      }

      function pintar(nuevo, anunciar) {
        activo = (nuevo % total + total) % total;
        colocarAromas(pista, activo);

        var frasco = frascos[activo];
        var elNombre = frasco.getAttribute('data-nombre') || '';
        var elTexto  = frasco.getAttribute('data-texto')  || '';

        // el texto se cambia con un fundido corto para que no salte
        [nombre, texto].forEach(function (el) {
          if (el) el.classList.add('is-cambiando');
        });

        window.setTimeout(function () {
          if (nombre) nombre.textContent = elNombre;
          if (texto)  texto.textContent  = elTexto;
          [nombre, texto].forEach(function (el) {
            if (el) el.classList.remove('is-cambiando');
          });
        }, SIN_MOVIMIENTO ? 0 : 180);

        puntos.forEach(function (punto, i) {
          punto.classList.toggle('is-activo', i === activo);
          punto.setAttribute('aria-selected', i === activo ? 'true' : 'false');
          punto.tabIndex = i === activo ? 0 : -1;
        });

        marcas.forEach(function (marca, i) {
          marca.classList.toggle('is-activo', i === activo);
        });

        precargarVecinos(activo);

        // Lectores de pantalla: solo al cambiar por acción del usuario
        if (anunciar && anuncio) anuncio.textContent = elNombre + '. ' + elTexto;
      }

      function mover(paso) { pintar(activo + paso, true); }

      if (antes)   antes.addEventListener('click',   function () { mover(-1); });
      if (despues) despues.addEventListener('click', function () { mover(1);  });

      frascos.forEach(function (frasco, i) {
        frasco.addEventListener('click', function () {
          if (i !== activo) pintar(i, true);
        });
      });

      puntos.forEach(function (punto, i) {
        punto.addEventListener('click', function () { pintar(i, true); });
      });

      // Teclado: flechas sobre el propio componente
      raiz.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); mover(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); mover(1);  }
        if (e.key === 'Home')       { e.preventDefault(); pintar(0, true); }
        if (e.key === 'End')        { e.preventDefault(); pintar(total - 1, true); }
      });

      // Swipe táctil
      var xInicio = null, yInicio = null;
      pista.addEventListener('touchstart', function (e) {
        xInicio = e.touches[0].clientX;
        yInicio = e.touches[0].clientY;
      }, { passive: true });

      pista.addEventListener('touchend', function (e) {
        if (xInicio === null) return;
        var dx = e.changedTouches[0].clientX - xInicio;
        var dy = e.changedTouches[0].clientY - yInicio;
        // solo cuenta como swipe si es claramente horizontal
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          mover(dx < 0 ? 1 : -1);
        }
        xInicio = null; yInicio = null;
      }, { passive: true });

      pintar(0, false);
    });
  }

  /* ---------------------------------------------------------------------
     Pestañas — motor compartido por el selector de momentos y el de zonas.

     Contrato del HTML:
       raíz              [data-cal-pestanas]
       botones           [data-cal-pestana]   (role="tab", con aria-controls)
       paneles           el elemento cuyo id apunta aria-controls
       fotos apiladas    [data-cal-pestana-media]  (opcional, por orden)
       matiz de fondo    data-ambiente en el botón (opcional)

     Sin JavaScript no se oculta nada: los paneles solo reciben el atributo
     hidden desde aquí, así que el contenido sigue siendo accesible.
     --------------------------------------------------------------------- */
  function initPestanas() {
    document.querySelectorAll('[data-cal-pestanas]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Pestanas')) return;

      var botones = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-pestana]'));
      var medias  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-pestana-media]'));
      if (botones.length < 2) return;

      var paneles = botones.map(function (b) {
        return document.getElementById(b.getAttribute('aria-controls'));
      });

      var activo = 0;

      function pintar(indice, mover) {
        activo = (indice % botones.length + botones.length) % botones.length;

        botones.forEach(function (b, i) {
          var esto = i === activo;
          b.setAttribute('aria-selected', esto ? 'true' : 'false');
          b.tabIndex = esto ? 0 : -1;
          b.classList.toggle('is-activo', esto);
        });

        paneles.forEach(function (p, i) {
          if (!p) return;
          if (i === activo) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });

        medias.forEach(function (m, i) {
          m.classList.toggle('is-activa', i === activo);
          m.setAttribute('aria-hidden', i === activo ? 'false' : 'true');
        });

        var ambiente = botones[activo].getAttribute('data-ambiente');
        if (ambiente) raiz.style.setProperty('--cal-ambiente', ambiente);

        if (mover) botones[activo].focus();
      }

      botones.forEach(function (b, i) {
        b.addEventListener('click', function () { pintar(i, false); });
      });

      // Flechas dentro de la lista de pestañas, como manda el patrón ARIA
      raiz.addEventListener('keydown', function (e) {
        if (botones.indexOf(document.activeElement) === -1) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); pintar(activo + 1, true); }
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); pintar(activo - 1, true); }
        if (e.key === 'Home') { e.preventDefault(); pintar(0, true); }
        if (e.key === 'End')  { e.preventDefault(); pintar(botones.length - 1, true); }
      });

      pintar(0, false);
    });
  }

  /* ---------------------------------------------------------------------
     Explorador de productos — el nombre elegido cambia la foto grande,
     el momento de uso, la microdescripción, el precio y el enlace.
     Escritorio: cursor y clic. Móvil: los mismos botones, más swipe.
     --------------------------------------------------------------------- */
  function initExplorador() {
    document.querySelectorAll('[data-cal-explorador]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Explorador')) return;

      var botones  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-exp-boton]'));
      var figuras  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-exp-figura]'));
      var detalles = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-exp-detalle]'));
      var media    = raiz.querySelector('[data-cal-exp-media]');
      if (botones.length < 2) return;

      var conCursor = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var activo = 0;

      function pintar(indice) {
        activo = (indice % botones.length + botones.length) % botones.length;

        botones.forEach(function (b, i) {
          var esto = i === activo;
          b.setAttribute('aria-selected', esto ? 'true' : 'false');
          b.tabIndex = esto ? 0 : -1;
          b.classList.toggle('is-activo', esto);
        });

        figuras.forEach(function (f, i) {
          f.classList.toggle('is-activa', i === activo);
          f.setAttribute('aria-hidden', i === activo ? 'false' : 'true');
        });

        adelantarImagen(figuras[activo + 1]);
        adelantarImagen(figuras[activo - 1]);

        detalles.forEach(function (d, i) {
          if (i === activo) d.removeAttribute('hidden');
          else d.setAttribute('hidden', '');
        });

        var fondo = botones[activo].getAttribute('data-fondo');
        if (fondo) raiz.style.setProperty('--cal-ambiente', fondo);
      }

      botones.forEach(function (b, i) {
        b.addEventListener('click', function () { pintar(i); });
        b.addEventListener('focus', function () { pintar(i); });
        if (conCursor) b.addEventListener('mouseenter', function () { pintar(i); });
      });

      raiz.addEventListener('keydown', function (e) {
        if (botones.indexOf(document.activeElement) === -1) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); botones[(activo + 1) % botones.length].focus(); }
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); botones[(activo - 1 + botones.length) % botones.length].focus(); }
      });

      // Swipe sobre la foto en pantallas táctiles
      if (media) {
        var xIni = null, yIni = null;
        media.addEventListener('touchstart', function (e) {
          xIni = e.touches[0].clientX; yIni = e.touches[0].clientY;
        }, { passive: true });
        media.addEventListener('touchend', function (e) {
          if (xIni === null) return;
          var dx = e.changedTouches[0].clientX - xIni;
          var dy = e.changedTouches[0].clientY - yIni;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) pintar(activo + (dx < 0 ? 1 : -1));
          xIni = null; yIni = null;
        }, { passive: true });
      }

      pintar(0);
    });
  }

  /* ---------------------------------------------------------------------
     El ritual — la foto se queda quieta mientras avanzan los pasos.
     En móvil el CSS desactiva el sticky y cada paso lleva su propia foto:
     aquí solo se marca el paso activo y se mueve el indicador.
     --------------------------------------------------------------------- */
  function initRitual() {
    document.querySelectorAll('[data-cal-ritual]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Ritual')) return;

      var pasos   = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-ritual-paso]'));
      var figuras = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-ritual-figura]'));
      var marcas  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-ritual-marca]'));
      if (!pasos.length) return;

      var actual = -1;

      function activar(indice) {
        /* Ahora esto se llama en cada fotograma de scroll: sin esta guarda
           estaríamos reescribiendo clases sesenta veces por segundo. */
        if (indice === actual) return;
        actual = indice;

        pasos.forEach(function (p, i) { p.classList.toggle('is-activo', i === indice); });
        figuras.forEach(function (f, i) {
          f.classList.toggle('is-activa', i === indice);
          f.setAttribute('aria-hidden', i === indice ? 'false' : 'true');
        });
        marcas.forEach(function (m, i) { m.classList.toggle('is-activa', i <= indice); });
        raiz.style.setProperty('--cal-ritual-progreso', ((indice + 1) / pasos.length * 100) + '%');

        adelantarImagen(figuras[indice + 1]);
        adelantarImagen(figuras[indice - 1]);
      }

      /* --- Qué paso toca --------------------------------------------------
         Antes esto lo decidía un IntersectionObserver con una franja de 10%
         en mitad de la pantalla, y ganaba el primer paso que la tocaba. Con
         pasos de 64vh eso adelantaba el cambio: la foto pasaba al paso
         siguiente cuando el texto del anterior seguía a un cuarto de pantalla,
         o sea, mientras aún se estaba leyendo.

         Ahora no se pregunta "quién toca la franja" sino "qué texto está más
         cerca del centro de la fotografía". Es determinista, da igual la
         velocidad del scroll y se comporta igual bajando que subiendo. */

      var escena = raiz.querySelector('.cal-rit__escena');

      function lineaDeLectura() {
        /* En escritorio la referencia es el centro de la foto fija. En móvil
           esa columna está oculta (alto 0) y se usa el centro de la pantalla. */
        if (escena) {
          var r = escena.getBoundingClientRect();
          if (r.height > 0) return r.top + r.height / 2;
        }
        return window.innerHeight / 2;
      }

      function pasoMasCercano() {
        var linea = lineaDeLectura();
        var elegido = 0;
        var minima = Infinity;

        for (var i = 0; i < pasos.length; i++) {
          var caja = pasos[i].querySelector('.cal-rit__paso-texto-caja') || pasos[i];
          var r = caja.getBoundingClientRect();
          var distancia = Math.abs(r.top + r.height / 2 - linea);
          if (distancia < minima) { minima = distancia; elegido = i; }
        }
        return elegido;
      }

      var pendiente = false;

      function alDesplazar() {
        /* El editor de temas reemplaza la sección entera al recargarla: si la
           raíz ya no está en el documento, este oyente sobra y se quita solo. */
        if (!document.contains(raiz)) {
          window.removeEventListener('scroll', alDesplazar);
          window.removeEventListener('resize', alDesplazar);
          return;
        }
        if (pendiente) return;
        pendiente = true;
        requestAnimationFrame(function () {
          pendiente = false;
          activar(pasoMasCercano());
        });
      }

      window.addEventListener('scroll', alDesplazar, { passive: true });
      window.addEventListener('resize', alDesplazar, { passive: true });
      alDesplazar();
    });
  }

  /* ---------------------------------------------------------------------
     Puntos interactivos sobre una foto de producto.
     Cursor, clic, teclado y toque. La foto entera se mantiene visible.
     --------------------------------------------------------------------- */
  function initPuntos() {
    document.querySelectorAll('[data-cal-puntos]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Puntos')) return;

      var puntos  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-punto]'));
      var paneles = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-punto-panel]'));
      var atajos  = Array.prototype.slice.call(raiz.querySelectorAll('[data-cal-punto-atajo]'));
      if (!puntos.length) return;

      var conCursor = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var activo = 0;

      function pintar(indice) {
        activo = (indice % puntos.length + puntos.length) % puntos.length;

        puntos.forEach(function (p, i) {
          var esto = i === activo;
          p.classList.toggle('is-activo', esto);
          p.setAttribute('aria-expanded', esto ? 'true' : 'false');
        });
        atajos.forEach(function (a, i) {
          var esto = i === activo;
          a.classList.toggle('is-activo', esto);
          a.setAttribute('aria-selected', esto ? 'true' : 'false');
          a.tabIndex = esto ? 0 : -1;
        });
        paneles.forEach(function (p, i) {
          if (i === activo) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      }

      puntos.forEach(function (p, i) {
        p.addEventListener('click', function () { pintar(i); });
        p.addEventListener('focus', function () { pintar(i); });
        if (conCursor) p.addEventListener('mouseenter', function () { pintar(i); });
      });

      atajos.forEach(function (a, i) {
        a.addEventListener('click', function () { pintar(i); });
      });

      raiz.addEventListener('keydown', function (e) {
        var lista = puntos.indexOf(document.activeElement) !== -1 ? puntos
                  : (atajos.indexOf(document.activeElement) !== -1 ? atajos : null);
        if (!lista) return;

        var paso = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') paso = 1;
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   paso = -1;
        if (!paso) return;

        e.preventDefault();
        // El destino se calcula ANTES de pintar: pintar() mueve `activo`, y
        // calcularlo después hacía avanzar dos posiciones de una tecla.
        var destino = (activo + paso + lista.length) % lista.length;
        pintar(destino);
        lista[destino].focus();
      });

      pintar(0);
    });
  }

  /* ---------------------------------------------------------------------
     Comparador de dos imágenes.
     El divisor es un <input type="range"> de verdad: así el arrastre con
     ratón, el arrastre con el dedo y las flechas del teclado los resuelve
     el propio navegador, sin reinventarlos.
     --------------------------------------------------------------------- */
  function initComparador() {
    document.querySelectorAll('[data-cal-comparador]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Comparador')) return;

      var rango = raiz.querySelector('[data-cal-comp-rango]');
      if (!rango) return;

      function pintar() {
        raiz.style.setProperty('--cal-comp-pos', rango.value + '%');
        rango.setAttribute('aria-valuetext', 'Divisor al ' + rango.value + ' por ciento');
      }

      rango.addEventListener('input', pintar);
      pintar();
    });
  }

  /* ---------------------------------------------------------------------
     Control de presión — la pera manual.
     "Tu presión. Tu decisión." Es una microinteracción explicativa: enseña
     cómo se regula, no promete ningún efecto.
     --------------------------------------------------------------------- */
  function initPresion() {
    document.querySelectorAll('[data-cal-presion]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Presion')) return;

      var rango  = raiz.querySelector('[data-cal-presion-rango]');
      var mas    = raiz.querySelector('[data-cal-presion-mas]');
      var menos  = raiz.querySelector('[data-cal-presion-menos]');
      var nivel  = raiz.querySelector('[data-cal-presion-nivel]');
      if (!rango) return;

      var etiquetas = (raiz.getAttribute('data-etiquetas') || 'Suave,Media,Firme').split(',');

      function pintar() {
        var v = parseInt(rango.value, 10);
        raiz.style.setProperty('--cal-presion', v + '%');

        var i = Math.min(etiquetas.length - 1, Math.floor(v / (100 / etiquetas.length)));
        var texto = (etiquetas[i] || '').trim();
        if (nivel) nivel.textContent = texto;
        rango.setAttribute('aria-valuetext', texto);
      }

      function empujar(paso) {
        rango.value = Math.max(0, Math.min(100, parseInt(rango.value, 10) + paso));
        pintar();
      }

      rango.addEventListener('input', pintar);
      if (mas)   mas.addEventListener('click',   function () { empujar(10); });
      if (menos) menos.addEventListener('click', function () { empujar(-10); });

      pintar();
    });
  }

  /* ---------------------------------------------------------------------
     Vídeo bajo demanda — hasta que alguien pulsa play solo hay una imagen.
     Nada de descargar megas de vídeo a quien nunca va a verlo.
     --------------------------------------------------------------------- */
  function initMedia() {
    document.querySelectorAll('[data-cal-media]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Media')) return;

      var boton = raiz.querySelector('[data-cal-media-play]');
      var fuente = raiz.querySelector('[data-cal-media-fuente]');
      if (!boton || !fuente) return;

      boton.addEventListener('click', function () {
        raiz.appendChild(fuente.content.cloneNode(true));
        boton.remove();

        var video = raiz.querySelector('video');
        if (video && video.play) {
          var intento = video.play();
          if (intento && intento.catch) intento.catch(function () { /* el navegador decide */ });
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     Bloque de compra — precio, disponibilidad y cantidad.

     El alta en el carrito la hace <product-form> de Dawn. Aquí solo se
     mantiene la ficha coherente al cambiar de variante: precio, tachado,
     estado del botón y parámetro ?variant= de la URL. Ningún importe se
     escribe a mano: todos vienen ya formateados desde Liquid.
     --------------------------------------------------------------------- */
  function initCompra() {
    document.querySelectorAll('[data-cal-compra]').forEach(function (raiz) {
      if (!primeraVez(raiz, 'Compra')) return;

      /* --- Cantidad --- */
      var campo = raiz.querySelector('[data-cal-cantidad-campo]');
      if (campo) {
        raiz.querySelectorAll('[data-cal-cantidad]').forEach(function (boton) {
          boton.addEventListener('click', function () {
            var paso = parseInt(boton.getAttribute('data-cal-cantidad'), 10) || 0;
            var valor = (parseInt(campo.value, 10) || 1) + paso;
            campo.value = Math.max(1, valor);
            campo.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        campo.addEventListener('change', function () {
          if ((parseInt(campo.value, 10) || 0) < 1) campo.value = 1;
        });
      }

      /* --- Variantes --- */
      var selector = raiz.querySelector('[data-cal-variante-select]');
      var datos    = raiz.querySelector('[data-cal-variantes-datos]');
      if (!selector || !datos) return;

      var variantes;
      try { variantes = JSON.parse(datos.textContent); } catch (e) { return; }

      var actual = raiz.querySelector('[data-cal-precio-actual]');
      var antes  = raiz.querySelector('[data-cal-precio-antes]');
      var boton  = raiz.querySelector('[data-cal-boton]');
      var texto  = boton ? boton.querySelector('span') : null;
      var caja   = raiz.querySelector('[data-cal-precio]');
      var mostrarTachado = caja ? caja.hasAttribute('data-cal-tachado') : false;

      selector.addEventListener('change', function () {
        var id = parseInt(selector.value, 10);
        var v = null;
        for (var i = 0; i < variantes.length; i++) {
          if (variantes[i].id === id) { v = variantes[i]; break; }
        }
        if (!v) return;

        if (actual) actual.textContent = v.precio;

        if (antes) {
          if (mostrarTachado && v.rebajado) {
            antes.textContent = v.antes;
            antes.removeAttribute('hidden');
          } else {
            antes.setAttribute('hidden', '');
          }
        }

        if (boton) {
          boton.disabled = !v.disponible;
          if (texto) texto.textContent = v.disponible ? boton.getAttribute('data-texto') || texto.textContent : 'Agotado';
        }

        // Que la URL apunte a la variante elegida, para poder compartirla
        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     FAQ — acordeón accesible
     --------------------------------------------------------------------- */
  function initFaq() {
    var preguntas = document.querySelectorAll('[data-cal-faq-boton]');
    if (!preguntas.length) return;

    preguntas.forEach(function (boton) {
      if (!primeraVez(boton, 'Faq')) return;
      var panel = document.getElementById(boton.getAttribute('aria-controls'));
      if (!panel) return;

      boton.addEventListener('click', function () {
        var abierto = boton.getAttribute('aria-expanded') === 'true';
        boton.setAttribute('aria-expanded', abierto ? 'false' : 'true');
        panel.style.maxHeight = abierto ? null : panel.scrollHeight + 'px';
        panel.classList.toggle('is-abierto', !abierto);
      });
    });

    // Si el contenido cambia de alto (rotación de móvil), recalcular.
    // El listener va a window, así que se registra UNA vez en toda la vida de
    // la página: initFaq() se vuelve a llamar en cada recarga de sección del
    // editor, y sin esta bandera se iría acumulando una copia por recarga.
    if (!faqEscuchandoResize) {
      faqEscuchandoResize = true;
      window.addEventListener('resize', function () {
        document.querySelectorAll('.cal-faq__panel.is-abierto').forEach(function (panel) {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Galería de producto — miniaturas
     --------------------------------------------------------------------- */
  function initGaleria() {
    var galerias = document.querySelectorAll('[data-cal-galeria]');
    if (!galerias.length) return;

    galerias.forEach(function (galeria) {
      if (!primeraVez(galeria, 'Galeria')) return;
      var principal = galeria.querySelector('[data-cal-galeria-principal]');
      var minis = galeria.querySelectorAll('[data-cal-galeria-mini]');
      if (!principal || !minis.length) return;

      var peticion = 0;

      minis.forEach(function (mini) {
        mini.addEventListener('click', function () {
          var src = mini.getAttribute('data-src');
          var alt = mini.getAttribute('data-alt') || '';
          if (!src || principal.getAttribute('src') === src) return;

          minis.forEach(function (m) { m.classList.remove('is-activa'); });
          mini.classList.add('is-activa');

          /* La foto nueva se descarga ANTES de cambiarla. Si se cambiaba el
             src a ciegas, durante la descarga seguía viéndose la anterior y
             el cambio llegaba a destiempo: ese era el salto al pulsar. */
          var miPeticion = ++peticion;
          var previa = new Image();

          function poner() {
            if (miPeticion !== peticion) return;   // ya se pulsó otra miniatura
            principal.src = src;
            principal.alt = alt;
            principal.style.opacity = '1';
          }

          principal.style.opacity = SIN_MOVIMIENTO ? '1' : '0';
          previa.onload = poner;
          previa.onerror = poner;   // si falla, se intenta igual y no se queda en blanco
          previa.src = src;

          // Si ya estaba en caché, onload puede haber saltado antes de asignarlo
          if (previa.complete) poner();
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Cala — el asistente de la tienda.

     Aquí solo se recoge lo que escribe el cliente y se pinta la respuesta. La
     clave de la IA no pasa por el navegador: se llama a un worker propio que
     es quien habla con Google.
     --------------------------------------------------------------------- */
  function initChat() {
    var chats = document.querySelectorAll('[data-cal-chat]');
    if (!chats.length) return;

    chats.forEach(function (chat) {
      if (chat.dataset.calListo === '1') return;   // no montar dos veces
      chat.dataset.calListo = '1';

      var abrir     = chat.querySelector('[data-cal-chat-abrir]');
      var panel     = chat.querySelector('.cal-chat__panel');
      var mensajes  = chat.querySelector('[data-cal-chat-mensajes]');
      var formulario = chat.querySelector('[data-cal-chat-form]');
      var entrada   = chat.querySelector('[data-cal-chat-entrada]');
      var enviar    = chat.querySelector('.cal-chat__enviar');
      var sugeridas = chat.querySelector('[data-cal-chat-sugeridas]');
      var endpoint  = chat.getAttribute('data-endpoint');
      if (!abrir || !panel || !mensajes || !formulario || !entrada || !endpoint) return;

      var historial = [];
      var esperando = false;
      var saludado = false;

      // A Cala se le pide que escriba en texto liso, pero de vez en cuando se le
      // escapa el marcado de Markdown. Aquí se pinta como texto, así que los
      // asteriscos se verían tal cual. Esta es la red por debajo.
      function limpiarMarcado(texto) {
        return String(texto)
          .replace(/\*\*(.+?)\*\*/g, '$1')
          .replace(/__(.+?)__/g, '$1')
          .replace(/^\s{0,3}#{1,6}\s+/gm, '')
          .replace(/^\s{0,3}[-*+]\s+/gm, '');
      }

      function pintar(quien, texto) {
        var burbuja = document.createElement('div');
        burbuja.className = 'cal-chat__mensaje cal-chat__mensaje--' + quien;
        burbuja.textContent = quien === 'cala' ? limpiarMarcado(texto) : texto;
        mensajes.appendChild(burbuja);
        mensajes.scrollTop = mensajes.scrollHeight;
        return burbuja;
      }

      function pintarEscribiendo() {
        var burbuja = document.createElement('div');
        burbuja.className = 'cal-chat__mensaje cal-chat__mensaje--cala';
        burbuja.innerHTML = '<span class="cal-chat__puntos"><i></i><i></i><i></i></span>';
        burbuja.setAttribute('aria-label', 'Cala está escribiendo');
        mensajes.appendChild(burbuja);
        mensajes.scrollTop = mensajes.scrollHeight;
        return burbuja;
      }

      function bloquear(si) {
        esperando = si;
        entrada.disabled = si;
        if (enviar) enviar.disabled = si;
      }

      function preguntar(texto) {
        if (esperando || !texto.trim()) return;
        pintar('cliente', texto);
        historial.push({ quien: 'cliente', texto: texto });
        entrada.value = '';
        if (sugeridas) sugeridas.hidden = true;
        bloquear(true);

        var escribiendo = pintarEscribiendo();

        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mensaje: texto, historial: historial.slice(0, -1) })
        })
          .then(function (r) { return r.json(); })
          .then(function (datos) {
            escribiendo.remove();
            var respuesta = (datos && datos.respuesta) ||
              'Ahora mismo no puedo responderte. Escríbenos a contacto@caleroa.com.';
            pintar('cala', respuesta);
            historial.push({ quien: 'cala', texto: respuesta });
          })
          .catch(function () {
            escribiendo.remove();
            pintar('cala', 'Se me ha ido la conexión. Prueba otra vez, o escríbenos a contacto@caleroa.com.');
          })
          .then(function () {
            bloquear(false);
            entrada.focus();
          });
      }

      function abrirPanel() {
        panel.hidden = false;
        chat.classList.add('is-abierto');
        abrir.setAttribute('aria-expanded', 'true');
        if (!saludado) {
          saludado = true;
          var saludo = chat.getAttribute('data-saludo');
          if (saludo) pintar('cala', saludo);
        }
        entrada.focus();
      }

      function cerrarPanel() {
        panel.hidden = true;
        chat.classList.remove('is-abierto');
        abrir.setAttribute('aria-expanded', 'false');
        abrir.focus();
      }

      abrir.addEventListener('click', function () {
        if (panel.hidden) abrirPanel(); else cerrarPanel();
      });

      chat.querySelectorAll('[data-cal-chat-cerrar]').forEach(function (b) {
        b.addEventListener('click', cerrarPanel);
      });

      chat.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !panel.hidden) cerrarPanel();
      });

      formulario.addEventListener('submit', function (e) {
        e.preventDefault();
        preguntar(entrada.value);
      });

      chat.querySelectorAll('[data-cal-chat-sugerida]').forEach(function (b) {
        b.addEventListener('click', function () { preguntar(b.textContent.trim()); });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Arranque
     --------------------------------------------------------------------- */
  function arrancar() {
    initHeader();
    initReveals();
    initPestanas();      // selector de momentos y selector de zonas
    initExplorador();
    initRitual();
    initSelectorAromas();
    initPuntos();
    initComparador();
    initPresion();
    initMedia();
    initCompra();
    initFaq();
    initGaleria();
    initChat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  // El editor de Shopify recarga secciones sueltas sin recargar la página
  document.addEventListener('shopify:section:load', arrancar);
})();
